import {
    CurrentPhase,
    OfferPhaseData,
    OfferSummaryData,
    PhaseType,
    SubscriptionOffers,
    OfferDetails,
    OfferDetailsPriceAndTerm,
} from '@sxm-de/dev-kits/web-sdk/types';
import { getPhaseByType } from './get-phase-by-type';
import { calculateDate } from './calculate-date';

function getPriceAndTermByPhaseType(
    priceAndTerm: OfferDetailsPriceAndTerm | null | undefined,
    phaseType: PhaseType,
) {
    if (phaseType === 'Free' && priceAndTerm?.free) {
        return priceAndTerm?.free;
    } else if (phaseType === 'Introductory' && priceAndTerm?.introductory) {
        return priceAndTerm?.introductory;
    } else if (phaseType === 'AutoRenewing' && priceAndTerm?.autoRenewing) {
        return priceAndTerm?.autoRenewing;
    }
    return null;
}

function mapPhaseTypeToType(
    phaseType: PhaseType,
): 'Trial' | 'Promo' | 'SelfPay' {
    switch (phaseType) {
        case 'Free': {
            return 'Trial';
        }
        case 'Introductory': {
            return 'Promo';
        }
        case 'AutoRenewing': {
            return 'SelfPay';
        }
    }
}

export function getOfferSummaries(
    offers: SubscriptionOffers,
    currentPhase: CurrentPhase,
    offerDetailsSet: OfferDetails[],
    state: string,
): {
    offerPhases: OfferPhaseData[];
    offerTrial: OfferSummaryData | null;
    offerPromo: OfferSummaryData | null;
    offerSelfPay: OfferSummaryData | null;
} {
    const offerPhases: OfferPhaseData[] = [];
    const productIdMainActive =
        offers.active.products.find(({ productType }) => productType.main)
            ?.id || '';
    const priceAndTermActive = offerDetailsSet.find(
        ({ offer }) => offer.id === offers.active.id,
    )?.offer.priceAndTerm;

    /**
     * Filter active phases if the state of the sub is not 'active' because
     * active could contain additional phases that will not be hit
     * (if state is pending_cancellation for example)
     */
    const activePhasesToUse =
        state === 'active' ? offers.active.phases : [offers.active.phases[0]];
    // look at active
    activePhasesToUse.forEach((phase) => {
        const priceAndTerm = getPriceAndTermByPhaseType(
            priceAndTermActive,
            phase.type,
        );
        const lastSub = offerPhases[offerPhases.length - 1];
        let startsAt = '';
        let endsAt = '';
        if (currentPhase.type === phase.type) {
            startsAt = currentPhase.started;
            endsAt = currentPhase.ends;
        } else if (lastSub) {
            startsAt = lastSub.endsAt;
            endsAt = calculateDate(startsAt, priceAndTerm?.termLength || 1);
        }
        offerPhases.push({
            type: mapPhaseTypeToType(phase.type),
            offerId: offers.active.id,
            productIdMain: productIdMainActive,
            ...(priceAndTerm ? { ...priceAndTerm } : {}),
            startsAt,
            endsAt,
        });
    });
    // look at pending
    if (offers.pending) {
        const productIdMainPending =
            offers.pending.products.find(({ productType }) => productType.main)
                ?.id || '';
        const priceAndTermPending = offerDetailsSet.find(
            ({ offer }) => offer.id === offers.pending.id,
        )?.offer.priceAndTerm;
        offers.pending.phases.forEach((phase) => {
            const priceAndTerm = getPriceAndTermByPhaseType(
                priceAndTermPending,
                phase.type,
            );
            const lastSub = offerPhases[offerPhases.length - 1];
            let startsAt = '';
            let endsAt = '';
            if (lastSub) {
                startsAt = lastSub.endsAt;
                endsAt = calculateDate(startsAt, priceAndTerm?.termLength || 1);
            }
            offerPhases.push({
                type: mapPhaseTypeToType(phase.type),
                offerId: offers.pending.id,
                productIdMain: productIdMainPending,
                ...(priceAndTerm ? { ...priceAndTerm } : {}),
                startsAt,
                endsAt,
            });
        });
    }

    const phaseTrial = getPhaseByType('Free', offers);
    let offerTrial: OfferSummaryData | null = null;
    if (phaseTrial) {
        const {
            offerId,
            phase: { type },
            productIdMain,
        } = phaseTrial;

        const priceAndTerm = offerDetailsSet.find(
            ({ offer }) => offer.id === offerId,
        )?.offer.priceAndTerm?.free;
        if (priceAndTerm) {
            const { termLength, priceTerm, priceMonthly } = priceAndTerm;

            let startsAt = '';
            let endsAt = '';
            if (currentPhase.type === type) {
                startsAt = currentPhase.started;
                endsAt = currentPhase.ends;
            }
            offerTrial = {
                offerId,
                productIdMain,
                term: termLength,
                priceMonthly,
                priceTerm,
                startsAt,
                endsAt,
            };
        }
    }

    const phasePromo = getPhaseByType('Introductory', offers);
    let offerPromo: OfferSummaryData | null = null;
    if (phasePromo) {
        const {
            offerId,
            phase: { type },
            productIdMain,
        } = phasePromo;

        const priceAndTerm = offerDetailsSet.find(
            ({ offer }) => offer.id === offerId,
        )?.offer.priceAndTerm?.introductory;
        if (priceAndTerm) {
            const { termLength, priceTerm, priceMonthly } = priceAndTerm;

            let startsAt = '';
            let endsAt = '';
            if (currentPhase.type === type) {
                startsAt = currentPhase.started;
                endsAt = currentPhase.ends;
            } else if (offerTrial) {
                startsAt = offerTrial.endsAt;
                endsAt = calculateDate(startsAt, termLength);
            }
            offerPromo = {
                offerId,
                productIdMain,
                term: termLength,
                priceMonthly,
                priceTerm,
                startsAt,
                endsAt,
            };
        }
    }

    const phaseSelfPay = getPhaseByType('AutoRenewing', offers);
    let offerSelfPay = null;
    if (phaseSelfPay) {
        const {
            offerId,
            phase: { type },
            productIdMain,
        } = phaseSelfPay;

        const priceAndTerm = offerDetailsSet.find(
            ({ offer }) => offer.id === offerId,
        )?.offer.priceAndTerm?.autoRenewing;
        if (priceAndTerm) {
            const { termLength, priceTerm, priceMonthly } = priceAndTerm;

            let startsAt = '';
            let endsAt = '';
            if (currentPhase.type === type) {
                startsAt = currentPhase.started;
                endsAt = currentPhase.ends;
            } else if (offerPromo) {
                startsAt = offerPromo.endsAt;
                endsAt = calculateDate(startsAt, termLength);
            }
            offerSelfPay = {
                offerId,
                productIdMain,
                term: termLength,
                priceMonthly,
                priceTerm,
                startsAt,
                endsAt,
            };
        }
    }

    return {
        offerPhases,
        offerTrial,
        offerPromo,
        offerSelfPay,
    };
}
